/*
 * Copyright 2022 StarTree Inc.
 *
 * All rights reserved. Confidential and proprietary information of StarTree Inc.
 */
import { AxiosError } from "axios";
import { useCallback, useState } from "react";
import { Status } from "../actions.interfaces";
import { Dataset } from "../dto/datasets.interfaces";
import {
    CategoryTypes,
    Connection,
    ConnectionConfig,
    ConnectionTypes,
    EditableConnection,
    ExploreDataSourcesArgsType,
    InferConfigArgsType,
    InferSchemaArgsType,
    InferSchemaFromSampleArgsType,
    Issues,
    PreviewIngestionArgsType,
    RunIngestionArgsType,
    TestConnectionArgsType,
    TestConnectionResponseType,
    ValidateConfigArgsType,
    ValidateSchemaArgsType,
} from "../dto/datasources.interfaces";
import {
    CreateDataSource,
    DeleteDataSource,
    ExploreDataSources,
    FetchConnectionProperties,
    FetchConnectionPropertiesByType,
    FetchDataSource,
    FetchDataSourceCategories,
    FetchDataSources,
    FetchDataSourceTypes,
    FetchDataSourceTypesByCategory,
    FetchIngestionProperties,
    FetchIngestionPropertiesV2,
    FetchTenants,
    InferConfig,
    InferSchema,
    InferSchemaFromSample,
    PreviewIngestion,
    RunIngestion,
    TestConnection,
    ValidateConfig,
    ValidateSchema,
} from "./datasources.interfaces";
import {
    createDataSource,
    deleteDataSource,
    exploreDataSources,
    getConnectionProperties,
    getConnectionPropertiesByType,
    getDataSource,
    getDataSourceCategories,
    getDataSources,
    getDataSourcesTypes,
    getDataSourcesTypesByCategory,
    getIngestionProperties,
    getIngestionPropertiesV2,
    getTenants,
    inferConfig,
    inferSchema,
    inferSchemaFromSample as inferSchemaFromSampleREST,
    previewIngestion as previewIngestionREST,
    runIngestion as runIngestionREST,
    testConnection as testConnectionREST,
    validateConfig as validateConfigREST,
    validateSchema as validateSchemaREST,
} from "./datasources.rest";

export const useCreateDataSource = (): CreateDataSource => {
    const [dataSource, setDataSource] =
        useState<CreateDataSource["dataSource"]>(null);
    const [errorMsg, setMessage] = useState("");
    const [status, setStatus] = useState(Status.INITIAL);

    const dispatchCreateDataSource = useCallback(
        async (connection: EditableConnection) => {
            setStatus(Status.FETCHING);

            try {
                const dataSource = await createDataSource(connection);

                setDataSource(dataSource);
                setMessage("");
                setStatus(Status.DONE);
            } catch (error) {
                const errorMessage = (error as AxiosError).response?.data
                    .message;

                setDataSource(null);
                setMessage(errorMessage);
                setStatus(Status.ERROR);
            }
        },
        []
    );

    return {
        createDataSource: dispatchCreateDataSource,
        dataSource,
        errorMsg,
        status,
    };
};

export const useDeleteDataSource = (): DeleteDataSource => {
    const [errorMsg, setMessage] = useState("");
    const [status, setStatus] = useState(Status.INITIAL);

    const dispatchDeleteDataSource = useCallback(
        async (srn: Dataset["srn"]) => {
            setStatus(Status.FETCHING);

            try {
                await deleteDataSource(srn);

                setMessage("");
                setStatus(Status.DONE);
            } catch (error) {
                const errorMessage = (error as AxiosError).response?.data
                    .message;

                setMessage(errorMessage);
                setStatus(Status.ERROR);
            }
        },
        []
    );

    return {
        deleteDataSource: dispatchDeleteDataSource,
        errorMsg,
        status,
    };
};

export const useExploreDataSources = (): ExploreDataSources => {
    const [sourceLocations, setSourceLocations] = useState<
        ExploreDataSources["sourceLocations"]
    >([]);
    const [errorMsg, setMessage] = useState("");
    const [status, setStatus] = useState(Status.INITIAL);

    const dispatchExploreDataSources = useCallback(
        async (data: ExploreDataSourcesArgsType) => {
            setStatus(Status.FETCHING);

            try {
                const sourceLocations = await exploreDataSources(data);

                setSourceLocations(sourceLocations.locations);
                setMessage("");
                setStatus(Status.DONE);
            } catch (error) {
                const errorMessage = (error as AxiosError).response?.data
                    .message;

                setSourceLocations([]);
                setMessage(errorMessage);
                setStatus(Status.ERROR);
            }
        },
        []
    );

    return {
        errorMsg,
        exploreDataSources: dispatchExploreDataSources,
        sourceLocations,
        status,
    };
};

export const useFetchConnectionProperties = (): FetchConnectionProperties => {
    const [connectionProperties, setConnectionProperties] = useState<
        FetchConnectionProperties["connectionProperties"]
    >([]);
    const [errorMsg, setMessage] = useState("");
    const [status, setStatus] = useState(Status.INITIAL);

    const fetchConnectionProperties = useCallback(async () => {
        setStatus(Status.FETCHING);

        try {
            const dataSourceProperties = await getConnectionProperties();

            setConnectionProperties(dataSourceProperties);
            setMessage("");
            setStatus(Status.DONE);
        } catch (error) {
            const errorMessage = (error as AxiosError).response?.data.message;

            setConnectionProperties([]);
            setMessage(errorMessage);
            setStatus(Status.ERROR);
        }
    }, []);

    return {
        connectionProperties,
        errorMsg,
        fetchConnectionProperties,
        status,
    };
};

export const useFetchConnectionPropertiesByType =
    (): FetchConnectionPropertiesByType => {
        const [connectionProperties, setConnectionProperties] = useState<
            FetchConnectionProperties["connectionProperties"]
        >([]);
        const [errorMsg, setMessage] = useState("");
        const [status, setStatus] = useState(Status.INITIAL);

        const fetchConnectionPropertiesByType = useCallback(
            async (type: ConnectionTypes) => {
                setStatus(Status.FETCHING);

                try {
                    const dataSourceProperties =
                        await getConnectionPropertiesByType(type);

                    setConnectionProperties(dataSourceProperties);
                    setMessage("");
                    setStatus(Status.DONE);
                } catch (error) {
                    const errorMessage = (error as AxiosError).response?.data
                        .message;

                    setConnectionProperties([]);
                    setMessage(errorMessage);
                    setStatus(Status.ERROR);
                }
            },
            []
        );

        return {
            connectionProperties,
            errorMsg,
            fetchConnectionPropertiesByType,
            status,
        };
    };

export const useFetchDataSource = (): FetchDataSource => {
    const [dataSource, setDataSource] = useState<Connection | null>(null);
    const [errorMsg, setMessage] = useState("");
    const [status, setStatus] = useState(Status.INITIAL);

    const fetchDataSource = useCallback(async (srn: string) => {
        setStatus(Status.FETCHING);

        try {
            const dataSource = await getDataSource(srn);

            setDataSource(dataSource);
            setMessage("");
            setStatus(Status.DONE);
        } catch (error) {
            const errorMessage = (error as AxiosError).response?.data.message;

            setDataSource(null);
            setMessage(errorMessage);
            setStatus(Status.ERROR);
        }
    }, []);

    return {
        dataSource,
        errorMsg,
        fetchDataSource,
        status,
    };
};

export const useFetchDataSourceCategories = (): FetchDataSourceCategories => {
    const [dataSourceCategories, setDataSourceCategories] = useState<
        FetchDataSourceCategories["dataSourceCategories"]
    >([]);
    const [errorMsg, setMessage] = useState("");
    const [status, setStatus] = useState(Status.INITIAL);

    const fetchDataSourceCategories = useCallback(async () => {
        setStatus(Status.FETCHING);

        try {
            const dataSourceCategoriesResponse =
                await getDataSourceCategories();

            setDataSourceCategories(dataSourceCategoriesResponse);
            setMessage("");
            setStatus(Status.DONE);
        } catch (error) {
            const errorMessage = (error as AxiosError).response?.data.message;

            setDataSourceCategories([]);
            setMessage(errorMessage);
            setStatus(Status.ERROR);
        }
    }, []);

    return {
        dataSourceCategories,
        errorMsg,
        fetchDataSourceCategories,
        status,
    };
};

export const useFetchDataSources = (): FetchDataSources => {
    const [dataSources, setDataSources] = useState<Connection[]>([]);
    const [errorMsg, setMessage] = useState("");
    const [status, setStatus] = useState(Status.INITIAL);

    const fetchDataSources = useCallback(async () => {
        setStatus(Status.FETCHING);

        try {
            const dataSources = await getDataSources();

            setDataSources(dataSources);
            setMessage("");
            setStatus(Status.DONE);
        } catch (error) {
            const errorMessage = (error as AxiosError).response?.data.message;

            setDataSources([]);
            setMessage(errorMessage);
            setStatus(Status.ERROR);
        }
    }, []);

    return {
        dataSources,
        errorMsg,
        fetchDataSources,
        setDataSources,
        status,
    };
};

export const useFetchDataSourceTypes = (): FetchDataSourceTypes => {
    const [dataSourceTypes, setDataSourceTypes] = useState<
        FetchDataSourceTypes["dataSourceTypes"]
    >([]);
    const [errorMsg, setMessage] = useState("");
    const [status, setStatus] = useState(Status.INITIAL);

    const fetchDataSourceTypes = useCallback(async () => {
        setStatus(Status.FETCHING);

        try {
            const dataSourceTypesResponse = await getDataSourcesTypes();

            setDataSourceTypes(dataSourceTypesResponse);
            setMessage("");
            setStatus(Status.DONE);
        } catch (error) {
            const errorMessage = (error as AxiosError).response?.data.message;

            setDataSourceTypes([]);
            setMessage(errorMessage);
            setStatus(Status.ERROR);
        }
    }, []);

    return {
        dataSourceTypes,
        errorMsg,
        fetchDataSourceTypes,
        status,
    };
};

export const useFetchDataSourceTypesByCategory =
    (): FetchDataSourceTypesByCategory => {
        const [dataSourceTypesByCategory, setDataSourceTypesByCategory] =
            useState<
                FetchDataSourceTypesByCategory["dataSourceTypesByCategory"]
            >([]);
        const [errorMsg, setMessage] = useState("");
        const [status, setStatus] = useState(Status.INITIAL);

        const fetchDataSourceTypesByCategory = useCallback(
            async (categoryType: CategoryTypes) => {
                setStatus(Status.FETCHING);

                try {
                    const dataSourceTypesResponse =
                        await getDataSourcesTypesByCategory(categoryType);

                    setDataSourceTypesByCategory(dataSourceTypesResponse);
                    setMessage("");
                    setStatus(Status.DONE);
                } catch (error) {
                    const errorMessage = (error as AxiosError).response?.data
                        .message;

                    setDataSourceTypesByCategory([]);
                    setMessage(errorMessage);
                    setStatus(Status.ERROR);
                }
            },
            []
        );

        return {
            dataSourceTypesByCategory,
            errorMsg,
            fetchDataSourceTypesByCategory,
            status,
        };
    };

export const useFetchIngestionProperties = (): FetchIngestionProperties => {
    const [ingestionProperties, setIngestionProperties] = useState<
        FetchIngestionProperties["ingestionProperties"]
    >([]);
    const [errorMsg, setMessage] = useState("");
    const [status, setStatus] = useState(Status.INITIAL);

    const fetchIngestionProperties = useCallback(
        async (type: ConnectionTypes | "") => {
            setStatus(Status.FETCHING);

            try {
                const ingestionProperties = await getIngestionProperties(type);

                setIngestionProperties(ingestionProperties);
                setMessage("");
                setStatus(Status.DONE);
            } catch (error) {
                const errorMessage = (error as AxiosError).response?.data
                    .message;

                setIngestionProperties([]);
                setMessage(errorMessage);
                setStatus(Status.ERROR);
            }
        },
        []
    );

    return {
        errorMsg,
        fetchIngestionProperties,
        ingestionProperties,
        status,
    };
};

export const useFetchIngestionPropertiesV2 = (): FetchIngestionPropertiesV2 => {
    // Hook-related values
    const [errorMsg, setMessage] = useState("");
    const [status, setStatus] = useState(Status.INITIAL);
    // Action-related values
    const [ingestionProperties, setIngestionProperties] = useState<
        FetchIngestionPropertiesV2["ingestionProperties"]
    >([]);

    const fetchIngestionPropertiesV2 = useCallback(
        async (
            ingestionConnection: ConnectionConfig,
            dataSourceType: ConnectionTypes | ""
        ) => {
            setStatus(Status.FETCHING);
            // Reset values on dispatch
            setMessage("");
            setIngestionProperties([]);

            try {
                const ingestionProperties = await getIngestionPropertiesV2({
                    ingestionConnection,
                    dataSourceType,
                });

                setIngestionProperties(ingestionProperties);
                setStatus(Status.DONE);
            } catch (error) {
                const errorMessage = (error as AxiosError).response?.data
                    .message;

                setMessage(errorMessage);
                setStatus(Status.ERROR);
            }
        },
        []
    );

    return {
        errorMsg,
        fetchIngestionPropertiesV2,
        ingestionProperties,
        status,
    };
};

export const useFetchTenants = (): FetchTenants => {
    const [tenants, setTenants] = useState<FetchTenants["tenants"]>(null);
    const [errorMsg, setMessage] = useState("");
    const [status, setStatus] = useState(Status.INITIAL);

    const dispatchFetchTenants = useCallback(async (srn: Connection["srn"]) => {
        setStatus(Status.FETCHING);

        try {
            const tenantsResponse = await getTenants(srn);

            setTenants(tenantsResponse);
            setMessage("");
            setStatus(Status.DONE);
        } catch (error) {
            const errorMessage = (error as AxiosError).response?.data.message;

            setTenants(null);
            setMessage(errorMessage);
            setStatus(Status.ERROR);
        }
    }, []);

    return {
        tenants,
        errorMsg,
        getTenants: dispatchFetchTenants,
        status,
    };
};

export const useInferConfig = (): InferConfig => {
    const [configuration, setConfiguration] =
        useState<InferConfig["configuration"]>(null);
    const [errorMsg, setMessage] = useState("");
    const [status, setStatus] = useState(Status.INITIAL);

    const dispatchInferConfig = useCallback(
        async (args: InferConfigArgsType) => {
            setStatus(Status.FETCHING);

            try {
                const inferConfigResponse = await inferConfig(args);
                setConfiguration(inferConfigResponse);

                setMessage("");
                setStatus(Status.DONE);
            } catch (error) {
                const errorMessage = (error as AxiosError).response?.data
                    .message;

                setConfiguration(null);
                setMessage(errorMessage);
                setStatus(Status.ERROR);
            }
        },
        []
    );

    return {
        configuration,
        errorMsg,
        inferConfig: dispatchInferConfig,
        status,
    };
};

export const useInferSchema = (): InferSchema => {
    // Hook-related values
    const [errorMsg, setMessage] = useState("");
    const [status, setStatus] = useState(Status.INITIAL);
    // Action-related values
    const [schema, setSchema] = useState<InferSchema["schema"]>(null);

    const dispatchInferSchema = useCallback(
        async (args: InferSchemaArgsType) => {
            setStatus(Status.FETCHING);
            // Reset values on dispatch
            setMessage("");
            setSchema(null);

            try {
                const inferSchemaResponse = await inferSchema(args);

                setSchema(inferSchemaResponse);
                setStatus(Status.DONE);
            } catch (error) {
                const errorMessage = (error as AxiosError).response?.data
                    .message;

                setMessage(errorMessage);
                setStatus(Status.ERROR);
            }
        },
        []
    );

    return {
        errorMsg,
        inferSchema: dispatchInferSchema,
        schema,
        status,
    };
};

export const useInferSchemaFromSample = (): InferSchemaFromSample => {
    // Hook-related values
    const [errorMsg, setMessage] = useState("");
    const [status, setStatus] = useState(Status.INITIAL);
    // Action-related values
    const [schema, setSchema] = useState<InferSchemaFromSample["schema"]>(null);

    const dispatchInferSchemaFromSample = useCallback(
        async (args: InferSchemaFromSampleArgsType) => {
            setStatus(Status.FETCHING);
            // Reset values on dispatch
            setMessage("");
            setSchema(null);

            try {
                const inferSchemaChangeResponse =
                    await inferSchemaFromSampleREST(args);

                setSchema(inferSchemaChangeResponse);
                setStatus(Status.DONE);
            } catch (error) {
                const errorMessage = (error as AxiosError).response?.data
                    .message;

                setMessage(errorMessage);
                setStatus(Status.ERROR);
            }
        },
        []
    );

    return {
        errorMsg,
        inferSchemaFromSample: dispatchInferSchemaFromSample,
        schema,
        status,
    };
};

export const usePreviewIngestion = (): PreviewIngestion => {
    const [tableData, setTableData] =
        useState<PreviewIngestion["tableData"]>(null);
    const [errorMsg, setMessage] = useState("");
    const [status, setStatus] = useState(Status.INITIAL);

    const previewIngestion = useCallback(
        async (args: PreviewIngestionArgsType) => {
            setStatus(Status.FETCHING);

            try {
                const previewIngestionResponse = await previewIngestionREST(
                    args
                );

                setTableData(previewIngestionResponse);
                setMessage("");
                setStatus(Status.DONE);
            } catch (error) {
                const errorMessage = (error as AxiosError).response?.data
                    .message;

                setTableData(null);
                setMessage(errorMessage);
                setStatus(Status.ERROR);
            }
        },
        []
    );

    return {
        errorMsg,
        previewIngestion,
        tableData,
        status,
    };
};

export const useRunIngestion = (): RunIngestion => {
    // Hook-related values
    const [errorMsg, setErrorMsg] = useState("");
    const [status, setStatus] = useState(Status.INITIAL);
    // Action-related values
    const [messages, setMessages] = useState<RunIngestion["messages"]>([]);
    const [success, setSuccess] = useState(false);

    const runIngestion = useCallback(async (args: RunIngestionArgsType) => {
        setStatus(Status.FETCHING);
        // Reset values on dispatch
        setErrorMsg("");
        setMessages([]);
        setSuccess(false);

        try {
            const ingestionResponse = await runIngestionREST(args);

            setMessages(ingestionResponse.messages);
            setSuccess(ingestionResponse.success);
            setStatus(Status.DONE);
        } catch (error) {
            const errorMessage = (error as AxiosError).response?.data.message;

            setErrorMsg(errorMessage);
            setStatus(Status.ERROR);
        }
    }, []);

    return {
        errorMsg,
        messages,
        runIngestion,
        status,
        success,
    };
};

export const useTestConnection = (): TestConnection => {
    // Hook-related values
    const [message, setMessage] = useState("");
    const [status, setStatus] = useState(Status.INITIAL);
    // Action-related values
    const [messages, setMessages] = useState<
        TestConnectionResponseType["messages"]
    >([]);
    const [pass, setPassed] = useState(false);

    const testConnection = useCallback(async (data: TestConnectionArgsType) => {
        setStatus(Status.FETCHING);
        // Reset values on dispatch
        setMessage("");
        setMessages([]);
        setPassed(false);

        try {
            const testConnectionResponse = await testConnectionREST(data);

            setMessages(testConnectionResponse.messages);
            setPassed(testConnectionResponse.pass);

            setStatus(Status.DONE);
        } catch (error) {
            const errorMessage = (error as AxiosError).response?.data.message;

            setMessage(errorMessage);
            setStatus(Status.ERROR);
        }
    }, []);

    const resetTestConnection = useCallback(() => {
        setMessages([]);
        setPassed(false);
        setMessage("");
        setStatus(Status.INITIAL);
    }, []);

    return {
        errorMsg: message,
        messages,
        pass,
        resetTestConnection,
        status,
        testConnection,
    };
};

export const useValidateConfig = (): ValidateConfig => {
    // Hook-related values
    const [message, setMessage] = useState("");
    const [status, setStatus] = useState(Status.INITIAL);
    // Action-related values
    const [pass, setPassed] = useState(false);
    const [issues, setIssues] = useState<Issues[]>([]);

    const validateConfig = useCallback(async (args: ValidateConfigArgsType) => {
        setStatus(Status.FETCHING);
        // Reset values on dispatch
        setMessage("");
        setIssues([]);
        setPassed(false);

        try {
            const validateResponse = await validateConfigREST(args);

            setPassed(validateResponse.pass);
            setIssues(validateResponse.issues);
            setStatus(Status.DONE);
        } catch (error) {
            const errorMessage = (error as AxiosError).response?.data.message;

            setMessage(errorMessage);
            setStatus(Status.ERROR);
        }
    }, []);

    return {
        errorMsg: message,
        issues,
        pass,
        status,
        validateConfig,
    };
};

export const useValidateSchema = (): ValidateSchema => {
    // Hook-related values
    const [message, setMessage] = useState("");
    const [status, setStatus] = useState(Status.INITIAL);
    // Action-related values
    const [pass, setPassed] = useState(false);
    const [issues, setIssues] = useState<Issues[]>([]);

    const validateSchema = useCallback(async (args: ValidateSchemaArgsType) => {
        setStatus(Status.FETCHING);
        // Reset values on dispatch
        setMessage("");
        setIssues([]);
        setPassed(false);

        try {
            const validateResponse = await validateSchemaREST(args);

            setPassed(validateResponse.pass);
            setIssues(validateResponse.issues);
            setStatus(Status.DONE);
        } catch (error) {
            const errorMessage = (error as AxiosError).response?.data.message;

            setMessage(errorMessage);
            setStatus(Status.ERROR);
        }
    }, []);

    return {
        errorMsg: message,
        issues,
        pass,
        status,
        validateSchema,
    };
};
